// Resume PDF generation.
//
// Builds a real, text-based PDF with jsPDF (NOT a rasterised screenshot) so the
// output stays selectable, searchable and — most importantly — parseable by ATS
// software. Layout mirrors the .docx templates: same section order, same
// heading style (uppercase + rule underneath), same serif/sans split.
import { jsPDF } from 'jspdf';

// A4 portrait in points. Margins chosen to match the docx templates.
const PAGE = { W: 595.28, H: 841.89, M: 48 };
const CW = PAGE.W - PAGE.M * 2; // content width

// Per-template presentation. `order` mirrors each .docx's section sequence.
const TEMPLATES = {
  1: {
    // Professional
    font: 'times',
    body: 9.5,
    heading: 10.5,
    name: 20,
    order: ['education', 'summary', 'skills', 'experience', 'projects', 'certifications'],
    labels: { summary: 'Summary', skills: 'Skills' },
  },
  2: {
    // Modern
    font: 'helvetica',
    body: 9.5,
    heading: 10.5,
    name: 20,
    order: ['education', 'summary', 'skills', 'experience', 'projects', 'certifications'],
    labels: { summary: 'Summary', skills: 'Skills' },
  },
  3: {
    // Simple — no summary; technical skills last
    font: 'helvetica',
    body: 9.5,
    heading: 10.5,
    name: 20,
    order: ['education', 'experience', 'projects', 'skills', 'certifications'],
    labels: { skills: 'Technical Skills' },
  },
  4: {
    // Academic — summary first, education after projects, denser body
    font: 'times',
    body: 8.5,
    heading: 9.5,
    name: 18,
    order: ['summary', 'experience', 'projects', 'education', 'skills', 'certifications'],
    labels: { summary: 'Professional Summary', skills: 'Skills' },
  },
};

const clean = (v) => (v == null ? '' : String(v).replace(/<[^>]*>/g, '').trim());

// Split a multi-line description into clean bullet strings.
const toBullets = (text) =>
  clean(text)
    .split('\n')
    .map((l) => l.replace(/^[•·‣▪–-]\s*/, '').trim())
    .filter(Boolean);

export async function generateResumePdf(templateId, data) {
  const t = TEMPLATES[templateId] || TEMPLATES[1];
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  let y = PAGE.M;

  const setFont = (size, style = 'normal') => {
    doc.setFont(t.font, style);
    doc.setFontSize(size);
    doc.setTextColor(0, 0, 0);
  };

  // Start a new page when the next block wouldn't fit.
  const ensure = (needed) => {
    if (y + needed > PAGE.H - PAGE.M) {
      doc.addPage();
      y = PAGE.M;
    }
  };

  const writeWrapped = (text, size, style, x = PAGE.M, width = CW, gap = 2) => {
    setFont(size, style);
    const lines = doc.splitTextToSize(clean(text), width);
    const lh = size * 1.28;
    lines.forEach((line) => {
      ensure(lh);
      doc.text(line, x, y);
      y += lh;
    });
    y += gap;
  };

  const sectionHeading = (label) => {
    const lh = t.heading * 1.3;
    ensure(lh + 14);
    y += 6;
    setFont(t.heading, 'bold');
    doc.text(label.toUpperCase(), PAGE.M, y);
    y += 3;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.8);
    doc.line(PAGE.M, y, PAGE.M + CW, y);
    y += t.heading * 0.95;
  };

  const bulletList = (items, indent = 10) => {
    const size = t.body;
    const lh = size * 1.28;
    items.forEach((item) => {
      setFont(size, 'normal');
      const lines = doc.splitTextToSize(item, CW - indent - 8);
      lines.forEach((line, i) => {
        ensure(lh);
        if (i === 0) doc.text('•', PAGE.M + 2, y);
        doc.text(line, PAGE.M + indent + 4, y);
        y += lh;
      });
    });
    y += 2;
  };

  // ---- Header: name + contact line -----------------------------------------
  const name = clean(data.fullName || data.personalInfo?.name);
  if (name) {
    setFont(t.name, 'bold');
    doc.text(name.toUpperCase(), PAGE.W / 2, y + t.name * 0.8, { align: 'center' });
    y += t.name * 1.25;
  }
  const contact = [
    data.personalInfo?.location || data.location,
    data.personalInfo?.phone || data.phone,
    data.personalInfo?.email || data.email,
    data.personalInfo?.linkedin || data.linkedin,
    data.personalInfo?.portfolio || data.portfolio,
  ]
    .map(clean)
    .filter(Boolean)
    .join('  |  ');
  if (contact) {
    setFont(t.body, 'normal');
    const lines = doc.splitTextToSize(contact, CW);
    lines.forEach((line) => {
      doc.text(line, PAGE.W / 2, y, { align: 'center' });
      y += t.body * 1.3;
    });
  }
  y += 6;

  // ---- Section renderers ----------------------------------------------------
  const renderers = {
    summary: () => {
      const s = clean(data.summary || data.professionalSummary);
      if (!s) return;
      sectionHeading(t.labels.summary || 'Summary');
      writeWrapped(s, t.body, 'normal');
    },

    experience: () => {
      const list = (data.workExperiences || data.workExperience || []).filter(
        (e) => e && (e.company || e.position),
      );
      if (!list.length) return;
      sectionHeading('Experience');
      list.forEach((e) => {
        const lh = t.body * 1.32;
        ensure(lh * 2);
        // Academic puts the role on top; others lead with the company.
        const lead = templateId === 4 ? clean(e.position) : clean(e.company);
        const sub = templateId === 4 ? clean(e.company) : clean(e.position);
        setFont(t.body, 'bold');
        doc.text(lead || '', PAGE.M, y);
        if (e.duration) {
          setFont(t.body, 'bold');
          doc.text(clean(e.duration), PAGE.M + CW, y, { align: 'right' });
        }
        y += lh;
        if (sub) {
          setFont(t.body, 'italic');
          doc.text(sub, PAGE.M, y);
          y += lh;
        }
        const bullets = toBullets(e.description);
        if (bullets.length) bulletList(bullets);
        y += 3;
      });
    },

    projects: () => {
      const list = (data.projects || []).filter((p) => p && p.name);
      if (!list.length) return;
      sectionHeading('Projects');
      list.forEach((p) => {
        const lh = t.body * 1.32;
        ensure(lh);
        setFont(t.body, 'bold');
        const head = clean(p.technologies) ? `${clean(p.name)} | ${clean(p.technologies)}` : clean(p.name);
        doc.splitTextToSize(head, CW).forEach((line) => {
          ensure(lh);
          doc.text(line, PAGE.M, y);
          y += lh;
        });
        // Simple template shows technologies only — no project bullets.
        if (templateId !== 3) {
          const bullets = toBullets(p.description);
          if (bullets.length) bulletList(bullets);
        }
        y += 3;
      });
    },

    education: () => {
      const list = (data.education || []).filter((e) => e && (e.institution || e.degree));
      if (!list.length) return;
      sectionHeading('Education');
      list.forEach((e) => {
        const lh = t.body * 1.32;
        ensure(lh);
        const parts = [
          clean(e.degree) + (clean(e.field) ? `, ${clean(e.field)}` : ''),
          clean(e.institution),
        ].filter(Boolean);
        setFont(t.body, 'bold');
        doc.text(parts.join(', '), PAGE.M, y);
        if (e.year) {
          setFont(t.body, 'normal');
          doc.text(clean(e.year), PAGE.M + CW, y, { align: 'right' });
        }
        y += lh;
      });
      y += 3;
    },

    skills: () => {
      const raw = clean(data.skills);
      if (!raw) return;
      sectionHeading(t.labels.skills || 'Skills');
      const lines = raw
        .split('\n')
        .map((l) => l.replace(/^[•·‣▪–-]\s*/, '').replace(/\*\*/g, '').trim())
        .filter(Boolean);
      bulletList(lines);
    },

    certifications: () => {
      const c = clean(data.certifications);
      if (!c) return;
      sectionHeading('Certifications');
      const lines = c.split('\n').map((l) => l.trim()).filter(Boolean);
      if (lines.length > 1) bulletList(lines);
      else writeWrapped(c, t.body, 'normal');
    },
  };

  t.order.forEach((key) => renderers[key] && renderers[key]());

  return doc.output('blob');
}

export default { generateResumePdf };
